import { useState, useMemo, useCallback } from "react";
import { X, Eye, EyeOff, RotateCcw } from "lucide-react";
import { Link } from "./SiteLink";
import { EditableText } from "./EditableText";
import { useRelativePath } from "./TemplateContext";
import { useContentEditingSafe } from "./ContentEditingContext";
import { categories } from "../../storeSiteConfig";
import { HIDDEN_CATS_KEY, useVisibleCategories } from "./useVisibleCategories";

/**
 * CategoryBar — dark navy horizontal bar below the header.
 * Shows "all products" link + category links.
 * Active category is highlighted.
 *
 * In edit mode:
 *   - Category names are editable inline (click to edit).
 *   - Each category has a hide button (X on hover).
 *   - Hidden categories can be restored from a panel.
 *
 * In view mode:
 *   - Category names are links.
 *   - Hidden categories are not shown.
 */
export function CategoryBar() {
  const relativePath = useRelativePath();
  const ctx = useContentEditingSafe();
  const isEditMode = ctx?.isEditMode ?? false;
  const [showHiddenPanel, setShowHiddenPanel] = useState(false);

  const visibleCategories = useVisibleCategories();

  // ── Read hidden categories from content KV ──
  const hiddenSlugs = useMemo<string[]>(() => {
    if (!ctx) return [];
    try {
      const raw = ctx.getContent(HIDDEN_CATS_KEY, "[]");
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, [ctx]);

  const hiddenCategories = useMemo(
    () => categories.filter((cat) => hiddenSlugs.includes(cat.slug)),
    [hiddenSlugs],
  );

  const hideCategory = useCallback(
    (slug: string) => {
      if (!ctx) return;
      const newHidden = [...hiddenSlugs, slug];
      ctx.saveContent(HIDDEN_CATS_KEY, JSON.stringify(newHidden));
    },
    [ctx, hiddenSlugs],
  );

  const showCategory = useCallback(
    (slug: string) => {
      if (!ctx) return;
      const newHidden = hiddenSlugs.filter((s) => s !== slug);
      ctx.saveContent(HIDDEN_CATS_KEY, JSON.stringify(newHidden));
    },
    [ctx, hiddenSlugs],
  );

  const restoreAll = useCallback(() => {
    if (!ctx) return;
    ctx.saveContent(HIDDEN_CATS_KEY, "[]");
    setShowHiddenPanel(false);
  }, [ctx]);

  // If all categories are hidden and not in edit mode, hide the entire bar
  if (visibleCategories.length === 0 && !isEditMode) return null;

  return (
    <div className="bg-zap-navy w-full">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <nav className="flex items-center justify-center gap-1 overflow-x-auto py-0">
          {/* All products link */}
          <Link
            to="/catalog"
            className={`shrink-0 px-3 py-2.5 text-xs rounded-t-lg transition-colors ${
              relativePath === "/catalog"
                ? "bg-white/10 text-white"
                : "text-blue-200 hover:text-white"
            }`}
            style={{ fontWeight: 500 }}
          >
            {"כל המוצרים"}
          </Link>

          {visibleCategories.length > 0 && (
            <span className="text-blue-400/50 text-base shrink-0">|</span>
          )}

          {visibleCategories.map((cat) => {
            const isActive = relativePath === `/catalog/${cat.slug}`;
            const baseCls = `px-3 py-2.5 text-xs rounded-t-lg transition-colors ${
              isActive
                ? "bg-white/10 text-white"
                : "text-blue-200 hover:text-white"
            }`;

            return (
              <span key={cat.slug} className="shrink-0 flex items-center gap-0 relative group/cat">
                {isEditMode ? (
                  /* Edit mode: inline-editable text, no link wrapping */
                  <span className={baseCls}>
                    <EditableText
                      contentKey={`category-bar:cat:${cat.slug}:name`}
                      defaultValue={cat.name}
                      as="span"
                      className="text-inherit"
                      style={{ fontWeight: 500, fontSize: "inherit", lineHeight: "inherit" }}
                      compact
                      hideTagBadge
                    />
                  </span>
                ) : (
                  /* View mode: normal link */
                  <Link
                    to={`/catalog/${cat.slug}`}
                    className={baseCls}
                    style={{ fontWeight: 500 }}
                  >
                    <EditableText
                      contentKey={`category-bar:cat:${cat.slug}:name`}
                      defaultValue={cat.name}
                      as="span"
                      className="text-inherit"
                      style={{ fontWeight: "inherit", fontSize: "inherit", lineHeight: "inherit" }}
                      compact
                      hideTagBadge
                    />
                  </Link>
                )}

                {/* Hide button — only in edit mode */}
                {isEditMode && (
                  <button
                    type="button"
                    className="btnReset shrink-0 w-4 h-4 flex items-center justify-center rounded-full opacity-0 group-hover/cat:opacity-100 cursor-pointer -mr-1"
                    style={{ backgroundColor: "rgba(239,68,68,0.8)" }}
                    onClick={() => hideCategory(cat.slug)}
                    title={`הסתר קטגוריה: ${cat.name}`}
                  >
                    <X className="w-2.5 h-2.5 text-white" />
                  </button>
                )}
              </span>
            );
          })}

          {/* Edit mode: show hidden count + restore button */}
          {isEditMode && hiddenCategories.length > 0 && (
            <>
              <span className="text-blue-400/50 text-base shrink-0">|</span>
              <button
                type="button"
                className="btnReset shrink-0 flex items-center gap-1 px-2.5 py-1.5 text-[10px] text-amber-300 hover:text-amber-200 cursor-pointer rounded-lg"
                style={{ backgroundColor: "rgba(255,255,255,0.08)" }}
                onClick={() => setShowHiddenPanel(!showHiddenPanel)}
              >
                <EyeOff className="w-3 h-3" />
                {`${hiddenCategories.length} קטגוריות מוסתרות`}
              </button>
            </>
          )}

          {isEditMode && hiddenCategories.length === 0 && (
            <span className="shrink-0 px-2 py-1.5 text-[10px] text-blue-300/50 italic">
              {"(לחצו על שם קטגוריה כדי לערוך)"}
            </span>
          )}
        </nav>

        {/* ── Hidden categories restore panel ── */}
        {isEditMode && showHiddenPanel && hiddenCategories.length > 0 && (
          <div className="border-t border-white/10 py-2 flex items-center gap-2 flex-wrap justify-center">
            <span className="text-blue-300/70 text-[10px] shrink-0" style={{ fontWeight: 600 }}>
              {"קטגוריות מוסתרות:"}
            </span>
            {hiddenCategories.map((cat) => (
              <button
                key={cat.slug}
                type="button"
                className="btnReset flex items-center gap-1 px-2 py-1 text-[10px] text-blue-200 rounded-md cursor-pointer hover:text-white"
                style={{ backgroundColor: "rgba(255,255,255,0.1)" }}
                onClick={() => showCategory(cat.slug)}
                title={`הצג קטגוריה: ${cat.name}`}
              >
                <Eye className="w-3 h-3" />
                <EditableText
                  contentKey={`category-bar:cat:${cat.slug}:name`}
                  defaultValue={cat.name}
                  as="span"
                  className="text-inherit"
                  style={{ fontWeight: "inherit", fontSize: "inherit" }}
                  compact
                  hideTagBadge
                />
              </button>
            ))}
            <button
              type="button"
              className="btnReset flex items-center gap-1 px-2 py-1 text-[10px] text-emerald-300 rounded-md cursor-pointer hover:text-emerald-200"
              style={{ backgroundColor: "rgba(255,255,255,0.1)" }}
              onClick={restoreAll}
              title="הצג את כל הקטגוריות"
            >
              <RotateCcw className="w-3 h-3" />
              {"שחזר הכל"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
